import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GET_CART,
  ADD_TO_CART,
  UPDATE_CART_ITEM,
  REMOVE_FROM_CART,
  CLEAR_CART,
  
} from "../../graphql/cartGQL";
import { executeGraphQL } from "../../graphql/api/cartApi";
interface CartItem {
  productId: string;
  quantity: number;
  price: number;
}

interface Cart {
  userId: string;
  items: CartItem[];
  totalPrice: number;
  lastUpdated: string;
}

interface CartState {
  cart: Cart | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: CartState = {
  cart: null,
  status: "idle",
  error: null,
};

// Get cart by user ID
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async (userId: string) => {
    const data = await executeGraphQL<{ getCart: Cart }>(GET_CART, { userId });
    return data.getCart;
  }
);

// Add to cart
export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ productId, userId, price }: { productId: string; userId: string ,price: number}) => {
    const data = await executeGraphQL<{ addToCart: Cart }>(ADD_TO_CART, {
      userId,
      input: {
        productId,
        quantity: 1,
        price: price // Price will be set by backend
      }
    });
    return data.addToCart;
  }
);

// Update cart item quantity
export const updateCartItem = createAsyncThunk(
  "cart/updateCartItem",
  async ({ userId, productId, quantity }: 
    { userId: string; productId: string; quantity: number }) => {
    const data = await executeGraphQL<{ updateCartItem: Cart }>(UPDATE_CART_ITEM, {
      userId,
      productId,
      quantity
    });
    return data.updateCartItem;
  }
);

// Remove from cart
export const removeFromCart = createAsyncThunk(
  "cart/removeFromCart",
  async ({ userId, productId }: { userId: string; productId: string }) => {
    const data = await executeGraphQL<{ removeFromCart: Cart }>(REMOVE_FROM_CART, {
      userId,
      productId
    });
    return data.removeFromCart;
  }
);

// Clear cart
export const clearCart = createAsyncThunk(
  "cart/clearCart",
  async (userId: string) => {
    await executeGraphQL<{ clearCart: boolean }>(CLEAR_CART, { userId });
    return userId;
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Cart
      .addCase(fetchCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cart = action.payload;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to fetch cart";
      })
      
      // Add to Cart
      .addCase(addToCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cart = action.payload;
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to add to cart";
      })
      
      // Update Cart Item
      .addCase(updateCartItem.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cart = action.payload;
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to update cart item";
      })
      
      // Remove from Cart
      .addCase(removeFromCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cart = action.payload;
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to remove from cart";
      })
      
      // Clear Cart
      .addCase(clearCart.pending, (state) => {
        state.status = "loading";
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.cart = {
          userId: action.payload,
          items: [],
          totalPrice: 0,
          lastUpdated: new Date().toISOString(),
        };
      })
      .addCase(clearCart.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || "Failed to clear cart";
      });
  },
});

export default cartSlice.reducer;